import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";

/**
 * App-level user-profile modal state. Plain-DOM grid cells (userTagDom.ts)
 * can't host React modals, so a click dispatches here and a single
 * UserProfileModalHost mounted in App.tsx renders the modal.
 */
/** An external (email-sourced) user with no account _id — e.g. a support ticket
 *  created from an email. Shown in the profile modal by fullName + email. */
export interface ExternalUserProfile {
  fullName?: string;
  email?: string;
}

export interface UserProfileModalState {
  /** User whose profile modal is open, or null when closed */
  userId: string | null;
  /** Ids shown in the grouped-users details modal, or null when closed */
  groupIds: string[] | null;
  /** Header for the grouped-users details modal */
  groupHeader?: string;
  /** External (no-_id) user whose profile modal is open, or null when closed */
  externalUser: ExternalUserProfile | null;
}

interface OpenGroupedUsersPayload {
  ids: string[];
  header?: string;
}

const initialState: UserProfileModalState = {
  userId: null,
  groupIds: null,
  groupHeader: undefined,
  externalUser: null,
};

const userProfileModalSlice = createSlice({
  name: "userProfileModal",
  initialState,
  reducers: {
    openUserProfile: (state: UserProfileModalState, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },
    openGroupedUsersModal: (state: UserProfileModalState, action: PayloadAction<OpenGroupedUsersPayload>) => {
      state.groupIds = action.payload.ids;
      state.groupHeader = action.payload.header;
    },
    openExternalUser: (state: UserProfileModalState, action: PayloadAction<ExternalUserProfile>) => {
      state.externalUser = action.payload;
    },
    closeUserProfile: (state: UserProfileModalState) => {
      state.userId = null;
    },
    closeExternalUser: (state: UserProfileModalState) => {
      state.externalUser = null;
    },
    closeUserProfileModals: (state: UserProfileModalState) => {
      state.userId = null;
      state.groupIds = null;
      state.groupHeader = undefined;
      state.externalUser = null;
    },
  },
});

const { openGroupedUsersModal: _openGroupedUsersModalRaw } = userProfileModalSlice.actions;

export const { openUserProfile, openExternalUser, closeUserProfile, closeExternalUser, closeUserProfileModals } =
  userProfileModalSlice.actions;

/** Object payload wrapped with `Exact<>` so excess properties are caught (see userTagData.ts). */
export const openGroupedUsersModal = <T extends Exact<OpenGroupedUsersPayload, T>>(payload: T) =>
  _openGroupedUsersModalRaw(payload);

export default userProfileModalSlice.reducer;
